import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import API from '../services/api';
import {
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import MapView from '../components/MapView';
import CaseAssignment from '../components/CaseAssignment';
import AiValidationPanel from '../components/AiValidationPanel';
import AiFieldMark from '../components/AiFieldMark';
import DeduplicationPanel from '../components/DeduplicationPanel';
import { SuggestionsProvider, SuggestionsCard, FieldSuggestion } from '../components/CaseSuggestions';
import { getViolationColor } from '../types/caseForm';

interface CaseData {
  id: string;
  caseNumber: string;
  status: string;
  rejectionCategory?: string | null;
  rejectionReason?: string | null;
  incidentDate: string;
  incidentTime?: string;
  reportedDate: string;
  description: string;
  violationTypes: string[];
  country: string;
  region?: string;
  city?: string;
  specificLocation?: string;
  latitude?: number;
  longitude?: number;
  indigenousGroup: string;
  indigenousTerritory?: string;
  priority: number;
  tags: string[];
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  submittedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  reviewedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    profilePicture?: string | null;
  };
  assignedAt?: string;

  // Development Project Information
  isRelatedToDevelopmentProject?: boolean;
  developmentProjectType?: string;
  developmentProjectName?: string;
  developmentProjectDescription?: string;
  developmentProjectStartYear?: number;
  developmentProjectSource?: string;
  developmentProjectWebsiteLink?: string;

  // Case Timeline
  isCaseOngoing?: boolean;
  caseStartDate?: string;
  caseStartDatePrecision?: 'DAY' | 'MONTH' | 'YEAR';
  caseEndDate?: string;

  // Violation Details (per-violation-type data)
  violationDetails: Array<{
    id: string;
    violationType: string;
    perpetratorKnown: boolean;
    perpetratorTypes: string[];
    perpetratorDetails?: string;
    legalCaseFiled: boolean;
    legalCaseDetails?: string;
    legalCaseWhyNot?: string;
    stateActionUndertaken: boolean;
    stateActionDetails?: string;
    stateActionWhyNot?: string;
    whyVictimsCriminalized?: string;
    harassmentPrior?: boolean;
    harassmentPriorDetails?: string;
    accusations?: string;
    policiesOrLawsUsed?: string;
    whoFiledAccusations?: string[];
    whoFiledAccusationsDetails?: string;
    accusationsLegallyFiled?: boolean;
    courtFilingLocation?: string;
    courtFilingDetails?: string;
    decisionOnAccusations?: boolean;
    decisionDetails?: string;
    victimsInPrison?: boolean;
    prisonConditions?: string;
    victimsSituationIfNotInPrison?: string;
    whyVictimsKilled?: string;
    howKillingCarriedOut?: string;
    cause?: string;
  }>;
  otherViolationTypes?: string[];
  otherViolationTypeDetails?: string;

  // Investigation and Advocacy (case-level)
  investigationConducted?: boolean;
  investigationDetails?: string;
  investigationWhyNot?: string;
  advocacyUndertaken?: boolean;
  advocacyDetails?: string;
  advocacyWhyNot?: string;

  // Impact
  familyImpact?: string;
  communityImpact?: string;

  // Source Information
  documentorName?: string;
  documentorOrganization?: string;
  documentorEmail?: string;
  documentorPhone?: string;
  isPrimaryData?: boolean;
  isAutoSourced?: boolean;
  dataSource?: string;
  dataUsageRestriction?: string;
  willingToShareMoreInfo?: boolean;
  isLDSFApplicant?: boolean;
  ldsfPaymentCompletedDate?: string;
  sourceInformation?: string;
  metadata?: {
    additionalSources?: Array<{ url: string; platform?: string | null; kind?: string; addedAt?: string }>;
    ai?: {
      autoApplied?: Array<{ field: string; value: unknown; priorValue: unknown; origin?: string; source?: string | null; snippet?: string; appliedAt?: string; undone?: boolean }>;
      conflicts?: Array<{ id?: string; field?: string; currentValue: unknown; proposedValue: unknown; origin?: string; source?: string | null; snippet?: string; resolved?: boolean; resolution?: unknown }>;
    };
  };

  victims: Array<{
    id: string;
    isGroup: boolean;
    violationDetails?: Array<{ violationType: string; otherViolationType?: string | null; affectedCount?: number | null; affectedMaleCount?: number | null; affectedFemaleCount?: number | null; affected0to12Count?: number | null; affected13to25Count?: number | null; affected26to35Count?: number | null; affected36to59Count?: number | null; affected60PlusCount?: number | null }>;
    firstName?: string;
    lastName?: string;
    ageBracket?: string;
    gender?: string;
    dependentType?: string;
    dependentDetails?: string;
    communityRole?: string;
    indigenousGroupOfVictim?: string;
    dateOfBirth?: string;
    additionalInfo?: string;
    // Group victim fields
    groupName?: string[];
    groupSize?: number;
    affectedIndividualsCount?: number;
    affectedFamiliesCount?: number;
    affectedHomesCount?: number;
    affectedGroupsCount?: number;
    affectedCommunitiesCount?: number;
    affectedVillagesCount?: number;
    affectedMaleCount?: number;
    affectedFemaleCount?: number;
    affected0to12Count?: number;
    affected13to25Count?: number;
    affected26to35Count?: number;
    affected36to59Count?: number;
    affected60PlusCount?: number;
  }>;
  perpetrators: Array<{
    id: string;
    isOrganization: boolean;
    name: string;
    type: string;
    affiliation?: string;
    notes?: string;
  }>;
}

const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [showAllSources, setShowAllSources] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [editingCaseInfo, setEditingCaseInfo] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState(0);
  const [editAssignee, setEditAssignee] = useState('');
  const [editReportedDate, setEditReportedDate] = useState('');
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; firstName: string; lastName: string; role: string }>>([]);
  const [submitErrors, setSubmitErrors] = useState<Array<{ field: string; message: string }>>([]);
  // Dedup summary lifted from DeduplicationPanel so we can show a page-level "possible duplicate" banner.
  const [dedupSummary, setDedupSummary] = useState<{ pending: number; confirmed: number }>({ pending: 0, confirmed: 0 });
  const handleDedupSummary = useCallback((s: { pending: number; confirmed: number }) => setDedupSummary(s), []);

  useEffect(() => {
    if (id) {
      fetchCase();
      fetchDocuments();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCase = async () => {
    try {
      const response = await API.get(`/api/cases/${id}`);
      setCaseData(response.data.case);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch case');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!id) return;

    setDocumentsLoading(true);
    try {
      const response = await API.get(`/api/documents/case/${id}`);
      setDocuments(response.data.documents || []);
    } catch (err: any) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await API.get('/api/users');
      const users = response.data.users || [];
      setAvailableUsers(users.filter((u: any) => ['RESEARCHER', 'APPROVER', 'ADMIN'].includes(u.role)));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAssignment = async (assignedToId: string | null) => {
    setUpdating(true);
    try {
      await API.put(`/api/cases/${id}/assign`, { assignedToId });
      await fetchCase();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to assign case');
    } finally {
      setUpdating(false);
    }
  };

  const downloadDocument = async (documentId: string) => {
    try {
      const response = await API.get(`/api/documents/${documentId}/download`);
      window.open(response.data.downloadUrl, '_blank');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to get download URL');
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await API.get(`/api/export/cases/${id}/pdf`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `case-${caseData?.caseNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Export PDF failed:', err);
      showToast('error', err.response?.data?.message || 'Failed to export case to PDF');
    }
  };

  // Reject flow: a required structured ground + free-text note, captured on the case.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCategory, setRejectCategory] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const rejectCase = async () => {
    if (!caseData || !rejectNote.trim()) return;
    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: 'REJECTED',
        rejectionCategory: rejectCategory || null,
        rejectionReason: rejectNote.trim(),
      });
      setCaseData(response.data.case);
      setRejectOpen(false);
      setRejectCategory('');
      setRejectNote('');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to reject case');
    } finally {
      setUpdating(false);
    }
  };

  const updateCaseStatus = async (newStatus: string) => {
    if (!caseData) return;

    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: newStatus
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        showToast('error', err.response.data.errors.map((e: any) => e.message || e.msg || `${e.field || e.path}: validation failed`));
      } else {
        showToast('error', err.response?.data?.message || 'Failed to update case status');
      }
    } finally {
      setUpdating(false);
    }
  };

  const submitCase = async () => {
    if (!caseData) return;

    setSubmitErrors([]);
    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: 'FOR_VALIDATION'
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setSubmitErrors(
          err.response.data.errors.map((e: any) => ({
            field: e.field || e.path || e.param || '',
            message: e.message || e.msg || ''
          }))
        );
      } else {
        setSubmitErrors([{
          field: '',
          message: err.response?.data?.message || 'Failed to submit case.'
        }]);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setUpdating(false);
    }
  };

  const updateCasePriority = async (newPriority: number) => {
    if (!caseData) return;

    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        priority: newPriority
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        showToast('error', err.response.data.errors.map((e: any) => e.message || e.msg || `${e.field || e.path}: validation failed`));
      } else {
        showToast('error', err.response?.data?.message || 'Failed to update case priority');
      }
    } finally {
      setUpdating(false);
    }
  };


  const deleteCase = async () => {
    const confirmed = await confirm({
      title: 'Delete Case',
      message: 'Are you sure you want to delete this case? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await API.delete(`/api/cases/${id}`);
      navigate('/cases');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to delete case');
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Med-low';
      case 3: return 'Medium';
      case 4: return 'Med-high';
      case 5: return 'High';
      default: return 'Unknown';
    }
  };

  const getViolationTypeLabel = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const canEdit = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' ||
           user.role === 'APPROVER' ||
           (user.role === 'RESEARCHER' && caseData.status !== 'APPROVED' && caseData.status !== 'FOR_APPROVAL') ||
           (user.id === caseData.submittedBy.id && caseData.status === 'DRAFT');
  };

  const canDelete = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' || 
           (user.id === caseData.submittedBy.id && caseData.status === 'DRAFT');
  };

  const canUpdateStatus = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' || user.role === 'APPROVER' || user.role === 'RESEARCHER';
  };

  const renderStatusActions = () => {
    if (!user || !caseData) return null;

    const role = user.role;
    const status = caseData.status;
    const isAdmin = role === 'ADMIN';

    const buttons: React.ReactNode[] = [];

    // DRAFT → FOR_VALIDATION (Documenter, Reviewer, Manager, Admin)
    if (status === 'DRAFT' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER' || user.id === caseData.submittedBy.id)) {
      buttons.push(
        <button
          key="submit"
          onClick={submitCase}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        >
          Submit Case
        </button>
      );
    }

    // FOR_VALIDATION → IN_RESEARCH (Researcher, Approver, Admin)
    if (status === 'FOR_VALIDATION' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER')) {
      buttons.push(
        <button
          key="start-review"
          onClick={() => updateCaseStatus('IN_RESEARCH')}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        >
          Start Review
        </button>
      );
    }

    // IN_RESEARCH → FOR_APPROVAL (Researcher, Approver, Admin)
    if (status === 'IN_RESEARCH' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER')) {
      buttons.push(
        <button
          key="validate"
          onClick={() => updateCaseStatus('FOR_APPROVAL')}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        >
          Mark For Approval
        </button>
      );
    }

    // FOR_APPROVAL → APPROVED, REJECTED, or return to IN_RESEARCH (Approver, Admin)
    if (status === 'FOR_APPROVAL' && (isAdmin || role === 'APPROVER')) {
      buttons.push(
        <button
          key="return-review"
          onClick={() => updateCaseStatus('IN_RESEARCH')}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Return for Research
        </button>,
        <button
          key="approve"
          onClick={() => updateCaseStatus('APPROVED')}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          Approve
        </button>,
        <button
          key="reject"
          onClick={() => { setRejectCategory(''); setRejectNote(''); setRejectOpen(true); }}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          Reject
        </button>
      );
    }

    // Reject directly from review — an APPROVER/ADMIN can reject without first advancing the case
    // to the approval stage. (The approval-stage Reject button above covers FOR_APPROVAL.)
    if ((status === 'FOR_VALIDATION' || status === 'IN_RESEARCH') && (isAdmin || role === 'APPROVER')) {
      buttons.push(
        <button
          key="reject-early"
          onClick={() => { setRejectCategory(''); setRejectNote(''); setRejectOpen(true); }}
          disabled={updating}
          className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          Reject
        </button>
      );
    }

    return buttons.length > 0 ? <>{buttons}</> : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3"></div>
          Loading case details...
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">Error loading case</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error || 'Case not found'}</p>
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Inline AI marker for a given field path — renders only if the AI auto-filled that field.
  // Track which fields got an inline AI mark this render, so the catch-all below can surface
  // any auto-filled field that isn't shown inline — guaranteeing none is ever invisible.
  const markedFields = new Set<string>();
  const humanizeField = (f: string): string => {
    const last = (f.split('.').pop() || f).replace(/\[.*?\]/g, '');
    return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
  };
  const aiMark = (field: string | string[]) => {
    (Array.isArray(field) ? field : [field]).forEach((f) => markedFields.add(f));
    return <AiFieldMark field={field} caseId={id!} ai={caseData.metadata?.ai} onChange={fetchCase} />;
  };
  // Renders inline marks for every auto-filled field NOT already marked above. Place at the bottom.
  const aiLeftovers = () => {
    const entries = ((caseData.metadata?.ai?.autoApplied as any[]) ?? []).filter(
      (e) => !e.undone && !e.superseded && e.field && !markedFields.has(e.field)
    );
    if (!entries.length) return null;
    // One badge per unique field — AiFieldMark already aggregates every entry for a field into a
    // single badge/popup, so mapping over raw entries would render duplicate badges (e.g. several
    // "Victims" adds each showing the same list). De-dupe to distinct fields, preserving order.
    const fields = Array.from(new Set(entries.map((e) => e.field)));
    return (
      <div className="mt-4 rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-3">
        <p className="text-xs font-semibold text-violet-800 dark:text-violet-300 mb-2">Other fields auto-filled by AI</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {fields.map((f) => (
            <span key={f} className="text-xs text-gray-700 dark:text-gray-300 inline-flex items-center gap-0.5">
              <span className="font-medium">{humanizeField(f)}:</span>{aiMark(f)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <SuggestionsProvider caseId={id!} caseData={caseData as Record<string, any>} onApplied={fetchCase}>
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Possible-duplicate banner — so an uncertain draft isn't mistaken for a normal case. */}
      {(dedupSummary.confirmed > 0 || dedupSummary.pending > 0) && (
        <div className={`mb-6 rounded-md border px-4 py-3 flex items-center gap-2 text-sm ${dedupSummary.confirmed > 0 ? 'border-red-300 bg-red-50 text-red-800' : 'border-yellow-300 bg-yellow-50 text-yellow-800'}`}>
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            {dedupSummary.confirmed > 0
              ? 'This case has been confirmed as a duplicate.'
              : 'Possible duplicate — pending review. Check the Case Duplicate Checker below before validating.'}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              {caseData.isPrimaryData !== undefined && (
                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded ${caseData.isPrimaryData ? 'bg-white text-green-700 ring-1 ring-green-300' : 'bg-white text-yellow-700 ring-1 ring-yellow-300'}`} title={caseData.isPrimaryData ? 'Primary data' : 'Secondary data'}>
                  {caseData.isPrimaryData ? 'P' : 'S'}
                </span>
              )}
              {caseData.caseNumber}
            </h1>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {caseData.violationTypes.map(type => (
                <span key={type} className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(type).soft}`}>
                  {getViolationTypeLabel(type)}
                </span>
              ))}
              {(caseData.otherViolationTypes || []).map(type => (
                <span key={`ov-${type}`} className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(type).soft}`}>
                  {getViolationTypeLabel(type)}
                </span>
              ))}
              {aiMark(['violationTypes', 'otherViolationTypes', 'violationDetails'])}
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={exportToPDF}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Export case to PDF"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export PDF
            </button>
            {canEdit() && (
              <Link
                to={`/cases/${id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </Link>
            )}
            {renderStatusActions()}
            {canDelete() && (
              <button
                onClick={deleteCase}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </button>
            )}
          </div>
        </div>

      </div>

      {submitErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-800">Case is not complete enough to submit</h3>
                <p className="text-sm text-red-700 mt-1">Please fix the following issues before submitting:</p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  {submitErrors.map((err, i) => (
                    <li key={i}>{err.message}</li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              onClick={() => setSubmitErrors([])}
              className="flex-shrink-0 text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Data Source & Documenter Information */}
          {(caseData.isAutoSourced || caseData.isPrimaryData !== undefined || caseData.documentorName || caseData.documentorPhone || caseData.dataSource || caseData.dataUsageRestriction || caseData.sourceInformation) && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Data Source & Documenter
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {caseData.isAutoSourced && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Origin</dt>
                    <dd className="mt-1">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800"
                        title="This case was created by IPRI's AI-assisted scraper from public sources. All content is subject to human review before approval."
                      >
                        <SparklesIcon className="h-3 w-3" />
                        Generated with AI assistance
                      </span>
                    </dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorName && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Documented By</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorName}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorOrganization && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorOrganization}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorEmail && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 break-words">{caseData.documentorEmail}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorPhone && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorPhone}</dd>
                  </div>
                )}
                {caseData.isPrimaryData !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Type</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${caseData.isPrimaryData ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {caseData.isPrimaryData ? 'Primary Data' : 'Secondary Data'}
                      </span>
                    </dd>
                  </div>
                )}
                {caseData.dataSource && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Source</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.dataSource}</dd>
                  </div>
                )}
                {caseData.dataUsageRestriction && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Usage Restriction</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      {caseData.dataUsageRestriction === 'IPRI_ONLY_TRENDS' && 'IPRI Only - Trends Analysis'}
                      {caseData.dataUsageRestriction === 'IPRI_WITH_DETAILS' && 'IPRI With Details - Can include case details publicly'}
                    </dd>
                  </div>
                )}
                {caseData.sourceInformation && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Source Info</dt>
                    <dd className="text-sm text-gray-700 dark:text-gray-300 break-words">{caseData.sourceInformation}</dd>
                  </div>
                )}
                {caseData.willingToShareMoreInfo !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Willing to Share More Info</dt>
                    <dd className={`text-sm font-medium ${caseData.willingToShareMoreInfo ? 'text-green-700' : 'text-gray-500'}`}>
                      {caseData.willingToShareMoreInfo ? 'Yes' : 'No'}
                    </dd>
                  </div>
                )}
                {caseData.isLDSFApplicant !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LDSF Applicant</dt>
                    <dd className={`text-sm font-medium ${caseData.isLDSFApplicant ? 'text-green-700' : 'text-gray-500'}`}>
                      {caseData.isLDSFApplicant ? 'Yes' : 'No'}
                    </dd>
                  </div>
                )}
                {caseData.ldsfPaymentCompletedDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LDSF Payment Completed Date</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      {new Date(caseData.ldsfPaymentCompletedDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Incident Details */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Incident Details</h2>
            <div className="space-y-4">
              {(caseData.isCaseOngoing !== undefined || caseData.caseStartDate || caseData.caseEndDate) && (
                <div className="space-y-3">
                  {caseData.isCaseOngoing !== undefined && (
                    <div className="flex items-center">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Status:</span>
                      <span className={`ml-2 px-3 py-1 rounded-full text-sm ${
                        caseData.isCaseOngoing
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {caseData.isCaseOngoing ? 'Ongoing' : 'Concluded'}
                      </span>
                      <FieldSuggestion field={['isCaseOngoing', 'caseEndDate']} />
                    </div>
                  )}
                  {caseData.caseStartDate && (
                    <div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Start Date:</span>
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {caseData.caseStartDatePrecision === 'YEAR'
                          ? new Date(caseData.caseStartDate).getFullYear()
                          : caseData.caseStartDatePrecision === 'MONTH'
                          ? new Date(caseData.caseStartDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
                          : new Date(caseData.caseStartDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {caseData.caseEndDate && (
                    <div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">End Date:</span>
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {new Date(caseData.caseEndDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {caseData.incidentTime && (
                    <div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Time:</span>
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{caseData.incidentTime}</span>
                    </div>
                  )}
                </div>
              )}

              {caseData.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Description<FieldSuggestion field="description" />{aiMark('description')}</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{caseData.description}</p>
                </div>
              )}

            </div>
          </div>

          {/* Location */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Location<FieldSuggestion field={['region', 'city', 'specificLocation']} />
            </h2>

            {/* Map View */}
            {caseData.latitude && caseData.longitude && (
              <div className="mb-6">
                <MapView
                  latitude={caseData.latitude}
                  longitude={caseData.longitude}
                  address={caseData.specificLocation}
                  height="250px"
                />
              </div>
            )}

            {/* Location Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Country:</span>{aiMark('country')} {caseData.country}
              </div>
              {caseData.region && (
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Region:</span>{aiMark('region')} {caseData.region}
                </div>
              )}
              {caseData.city && (
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">City:</span>{aiMark('city')} {caseData.city}
                </div>
              )}
              {caseData.specificLocation && (
                <div className="md:col-span-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Specific Location:</span>{aiMark('specificLocation')} {caseData.specificLocation}
                </div>
              )}
              {caseData.latitude && caseData.longitude && (
                <div className="md:col-span-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Coordinates:</span> {caseData.latitude.toFixed(6)}, {caseData.longitude.toFixed(6)}
                </div>
              )}
            </div>
          </div>

          {/* Indigenous Group */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Indigenous Group Information</h2>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Indigenous Group:</span>{aiMark('indigenousGroup')} {caseData.indigenousGroup}
              </div>
              {caseData.indigenousTerritory && (
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Territory:</span>{aiMark('indigenousTerritory')} {caseData.indigenousTerritory}
                </div>
              )}
            </div>
          </div>

          {/* Victims */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Victims<FieldSuggestion section="victims" /></h2>
            <div className="space-y-4">
              {(() => {
                const gv = caseData.victims.find(v => v.isGroup);
                return gv && (
                <div className="border border-gray-200 dark:border-gray-700 border-l-4 border-l-purple-500 bg-white dark:bg-gray-800/40 rounded-lg p-4 space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Group(s) of Victims</h3>
                  {gv.groupName && Array.isArray(gv.groupName) && gv.groupName.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {gv.groupName.map((group: string) => (
                        <span key={group} className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">{group}</span>
                      ))}
                    </div>
                  )}
                  {(gv.affectedIndividualsCount || gv.affectedFamiliesCount || gv.affectedHomesCount ||
                    gv.affectedGroupsCount || gv.affectedCommunitiesCount || gv.affectedVillagesCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Affected Population</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {gv.affectedIndividualsCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-emerald-600">{gv.affectedIndividualsCount}</div><div className="text-xs text-gray-600">Individuals</div></div>)}
                        {gv.affectedFamiliesCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-blue-600">{gv.affectedFamiliesCount}</div><div className="text-xs text-gray-600">Families</div></div>)}
                        {gv.affectedHomesCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-purple-600">{gv.affectedHomesCount}</div><div className="text-xs text-gray-600">Homes</div></div>)}
                        {gv.affectedGroupsCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-green-600">{gv.affectedGroupsCount}</div><div className="text-xs text-gray-600">Groups</div></div>)}
                        {gv.affectedCommunitiesCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-orange-600">{gv.affectedCommunitiesCount}</div><div className="text-xs text-gray-600">Communities</div></div>)}
                        {gv.affectedVillagesCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-red-600">{gv.affectedVillagesCount}</div><div className="text-xs text-gray-600">Villages</div></div>)}
                      </div>
                    </div>
                  )}
                  {Array.isArray(gv.violationDetails) && gv.violationDetails.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Breakdown by Violation</span>
                      {/* One badge above the scrollable table — an inline mark inside overflow-x-auto gets its popup clipped. */}
                      {aiMark(gv.violationDetails.map((t: any) => `victims[${gv.id}].violation[${t.otherViolationType || t.violationType}].affectedCount`))}
                      <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr className="text-left text-gray-500 dark:text-gray-400">
                              <th className="px-4 py-3 font-medium">Violation</th>
                              <th className="px-4 py-3 font-medium">Total</th>
                              <th className="px-4 py-3 font-medium">Male</th>
                              <th className="px-4 py-3 font-medium">Female</th>
                              <th className="px-4 py-3 font-medium">0–12</th>
                              <th className="px-4 py-3 font-medium">13–25</th>
                              <th className="px-4 py-3 font-medium">26–35</th>
                              <th className="px-4 py-3 font-medium">36–59</th>
                              <th className="px-4 py-3 font-medium">60+</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {gv.violationDetails.map((t: any, ti: number) => {
                              const key = t.otherViolationType || t.violationType;
                              const label = getViolationTypeLabel(key || '');
                              const cols = ['affectedMaleCount','affectedFemaleCount','affected0to12Count','affected13to25Count','affected26to35Count','affected36to59Count','affected60PlusCount'];
                              const cell = (v: any) => v == null ? <span className="text-gray-300 dark:text-gray-600">—</span> : v;
                              return (
                                <tr key={ti}>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(key).soft}`}>{label}</span>
                                  </td>
                                  <td className="px-4 py-3 font-bold text-gray-900 dark:text-gray-100">{cell(t.affectedCount)}</td>
                                  {cols.map(c => (
                                    <td key={c} className="px-4 py-3 text-gray-700 dark:text-gray-300">{cell(t[c])}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {(!Array.isArray(gv.violationDetails) || gv.violationDetails.length === 0) && (gv.affectedMaleCount || gv.affectedFemaleCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Gender Breakdown</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {gv.affectedMaleCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-blue-600">{gv.affectedMaleCount}</div><div className="text-xs text-gray-600">Male</div></div>)}
                        {gv.affectedFemaleCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-pink-600">{gv.affectedFemaleCount}</div><div className="text-xs text-gray-600">Female</div></div>)}
                      </div>
                    </div>
                  )}
                  {(!Array.isArray(gv.violationDetails) || gv.violationDetails.length === 0) && (gv.affected0to12Count || gv.affected13to25Count || gv.affected26to35Count ||
                    gv.affected36to59Count || gv.affected60PlusCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Age Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {gv.affected0to12Count != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-green-600">{gv.affected0to12Count}</div><div className="text-xs text-gray-600">0-12 years</div></div>)}
                        {gv.affected13to25Count != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-blue-600">{gv.affected13to25Count}</div><div className="text-xs text-gray-600">13-25 years</div></div>)}
                        {gv.affected26to35Count != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-purple-600">{gv.affected26to35Count}</div><div className="text-xs text-gray-600">26-35 years</div></div>)}
                        {gv.affected36to59Count != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-orange-600">{gv.affected36to59Count}</div><div className="text-xs text-gray-600">36-59 years</div></div>)}
                        {gv.affected60PlusCount != null && (<div className="text-center p-2 bg-white rounded-lg border border-purple-200"><div className="text-xl font-bold text-red-600">{gv.affected60PlusCount}</div><div className="text-xs text-gray-600">60+ years</div></div>)}
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
              {caseData.victims.some(v => !v.isGroup) && (
                <div className="border border-gray-200 dark:border-gray-700 border-l-4 border-l-slate-400 dark:border-l-slate-500 bg-white dark:bg-gray-800/40 rounded-lg p-4 space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Named Victim(s)</h3>
                  {caseData.victims.filter(v => !v.isGroup).map((victim) => (
                <div key={victim.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{victim.firstName} {victim.lastName}{aiMark(`victims[${victim.id}].firstName`)}{aiMark(`victims[${victim.id}].lastName`)}</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-2 mt-1">
                      {victim.dateOfBirth && <span>DOB:{aiMark(`victims[${victim.id}].dateOfBirth`)} {new Date(victim.dateOfBirth).toLocaleDateString()}</span>}
                      {victim.ageBracket && <span>Age Bracket:{aiMark(`victims[${victim.id}].ageBracket`)} {{ A: '0-12 years', B: '13-25 years', C: '26-35 years', D: '36-59 years', E: '60+ years' }[victim.ageBracket] || victim.ageBracket}</span>}
                      {victim.gender && <span>Gender:{aiMark(`victims[${victim.id}].gender`)} {victim.gender}</span>}
                      {victim.dependentType && <span>Dependents:{aiMark(`victims[${victim.id}].dependentType`)} {{ WD: 'With Dependents', WOD: 'Without Dependents', ND: 'No Data' }[victim.dependentType] || victim.dependentType}</span>}
                    </div>
                  </div>
                  {Array.isArray(victim.violationDetails) && victim.violationDetails.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 mr-1">Violations:</span>
                      {victim.violationDetails.map((t, ti) => (
                        <span key={ti} className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(t.otherViolationType || t.violationType).soft}`}>
                          {getViolationTypeLabel((t.otherViolationType || t.violationType) || '')}
                        </span>
                      ))}
                    </div>
                  )}
                  {victim.indigenousGroupOfVictim && (<div className="mt-2"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Indigenous Group:</span>{aiMark(`victims[${victim.id}].indigenousGroupOfVictim`)} <span className="text-sm text-gray-700 dark:text-gray-300">{victim.indigenousGroupOfVictim}</span></div>)}
                  {victim.communityRole && (<div className="mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Community Role:</span>{aiMark(`victims[${victim.id}].communityRole`)} <span className="text-sm text-gray-700 dark:text-gray-300">{victim.communityRole}</span></div>)}
                  {victim.dependentDetails && (<div className="mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dependent Details:</span>{aiMark(`victims[${victim.id}].dependentDetails`)} <span className="text-sm text-gray-700 dark:text-gray-300">{victim.dependentDetails}</span></div>)}
                  {victim.additionalInfo && (<div className="mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Additional Info:</span>{aiMark(`victims[${victim.id}].additionalInfo`)} <span className="text-sm text-gray-700 dark:text-gray-300">{victim.additionalInfo}</span></div>)}
                </div>
              ))}
                </div>
              )}
            </div>
          </div>

          {/* Criminalization-Specific Details */}
          {(() => {
            const crimDetail = caseData.violationDetails?.find(vd => vd.violationType === 'CRIMINALIZATION');
            return crimDetail && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-orange-500">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Criminalization Details<FieldSuggestion section="violations" />
              </h2>
              <div className="space-y-4">
                {crimDetail.whyVictimsCriminalized && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Why Victim(s) Criminalized{aiMark(`violationDetails[${crimDetail.id}].whyVictimsCriminalized`)}</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{crimDetail.whyVictimsCriminalized}</p>
                  </div>
                )}

                {crimDetail.harassmentPrior !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Harassment Prior to Criminalization</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      crimDetail.harassmentPrior
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {crimDetail.harassmentPrior ? '✓ Yes' : '✗ No'}
                    </span>
                    {crimDetail.harassmentPriorDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{crimDetail.harassmentPriorDetails}{aiMark(`violationDetails[${crimDetail.id}].harassmentPriorDetails`)}</p>
                    )}
                  </div>
                )}

                {crimDetail.accusations && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Accusations/Charges Filed</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].accusations`)} {crimDetail.accusations}</p>
                  </div>
                )}

                {crimDetail.policiesOrLawsUsed && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Policies or Laws Used</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].policiesOrLawsUsed`)} {crimDetail.policiesOrLawsUsed}</p>
                  </div>
                )}

                {crimDetail.whoFiledAccusations && crimDetail.whoFiledAccusations.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Who Filed Accusations</h3>
                    <div className="flex flex-wrap gap-2">
                      {crimDetail.whoFiledAccusations.map(w => (
                        <span key={w} className="px-3 py-1 rounded-lg text-sm bg-orange-100 text-orange-800">{getViolationTypeLabel(w)}</span>
                      ))}
                    </div>
                    {crimDetail.whoFiledAccusationsDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{crimDetail.whoFiledAccusationsDetails}</p>
                    )}
                  </div>
                )}

                {crimDetail.accusationsLegallyFiled !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Legally/Formally Filed</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      crimDetail.accusationsLegallyFiled
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {crimDetail.accusationsLegallyFiled ? '✓ Yes' : '✗ No'}
                    </span>
                    {crimDetail.courtFilingLocation && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Court Location:</span>{aiMark(`violationDetails[${crimDetail.id}].courtFilingLocation`)}
                        <p className="text-sm text-gray-700 dark:text-gray-300">{crimDetail.courtFilingLocation}</p>
                      </div>
                    )}
                    {crimDetail.courtFilingDetails && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Details:</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{crimDetail.courtFilingDetails}{aiMark(`violationDetails[${crimDetail.id}].courtFilingDetails`)}</p>
                      </div>
                    )}
                  </div>
                )}

                {crimDetail.decisionOnAccusations !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Decision on Accusations</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      crimDetail.decisionOnAccusations
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {crimDetail.decisionOnAccusations ? '✓ Yes' : '✗ No'}
                    </span>
                    {crimDetail.decisionDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{crimDetail.decisionDetails}</p>
                    )}
                  </div>
                )}

                {crimDetail.victimsInPrison !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Victim(s) in Prison/Detention</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      crimDetail.victimsInPrison
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {crimDetail.victimsInPrison ? '✓ Yes, in prison' : '✗ Not in prison'}
                    </span>
                    {crimDetail.prisonConditions && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Prison Conditions:</span>{aiMark(`violationDetails[${crimDetail.id}].prisonConditions`)}
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{crimDetail.prisonConditions}</p>
                      </div>
                    )}
                    {crimDetail.victimsSituationIfNotInPrison && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Current Situation:</span>{aiMark(`violationDetails[${crimDetail.id}].victimsSituationIfNotInPrison`)}
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{crimDetail.victimsSituationIfNotInPrison}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Shared fields: Perpetrator */}
                {crimDetail.perpetratorKnown !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Alleged Perpetrators Known</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      crimDetail.perpetratorKnown ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {crimDetail.perpetratorKnown ? '✓ Yes' : '✗ No'}
                    </span>
                    {crimDetail.perpetratorTypes && crimDetail.perpetratorTypes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {crimDetail.perpetratorTypes.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">{getViolationTypeLabel(t)}</span>
                        ))}
                        {aiMark(`violationDetails[${crimDetail.id}].perpetratorTypes`)}
                      </div>
                    )}
                    {crimDetail.perpetratorDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].perpetratorDetails`)} {crimDetail.perpetratorDetails}</p>
                    )}
                  </div>
                )}

                {/* Shared fields: Legal Case */}
                {crimDetail.legalCaseFiled !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Legal Case Filed: {crimDetail.legalCaseFiled ? '✓ Yes' : '✗ No'}
                    </h3>
                    {crimDetail.legalCaseFiled && crimDetail.legalCaseDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].legalCaseDetails`)} {crimDetail.legalCaseDetails}</p>
                    )}
                    {!crimDetail.legalCaseFiled && crimDetail.legalCaseWhyNot && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].legalCaseWhyNot`)} {crimDetail.legalCaseWhyNot}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Shared fields: State Action */}
                {crimDetail.stateActionUndertaken !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      State Action Undertaken: {crimDetail.stateActionUndertaken ? '✓ Yes' : '✗ No'}
                    </h3>
                    {crimDetail.stateActionUndertaken && crimDetail.stateActionDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${crimDetail.id}].stateActionDetails`)} {crimDetail.stateActionDetails}</p>
                    )}
                    {!crimDetail.stateActionUndertaken && crimDetail.stateActionWhyNot && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark(`violationDetails[${crimDetail.id}].stateActionWhyNot`)}
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{crimDetail.stateActionWhyNot}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Killing-Specific Details */}
          {(() => {
            const killDetail = caseData.violationDetails?.find(vd => vd.violationType === 'KILLING');
            return killDetail && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-red-600">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Killing Details<FieldSuggestion section="violations" />
              </h2>
              <div className="space-y-4">
                {killDetail.whyVictimsKilled && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Why Victim(s) Killed</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].whyVictimsKilled`)} {killDetail.whyVictimsKilled}</p>
                  </div>
                )}

                {killDetail.howKillingCarriedOut && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">How Killing Was Carried Out</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].howKillingCarriedOut`)} {killDetail.howKillingCarriedOut}</p>
                  </div>
                )}

                {killDetail.harassmentPrior !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Harassment Prior to Killing</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      killDetail.harassmentPrior
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {killDetail.harassmentPrior ? '✓ Yes' : '✗ No'}
                    </span>
                    {killDetail.harassmentPriorDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].harassmentPriorDetails`)} {killDetail.harassmentPriorDetails}</p>
                    )}
                  </div>
                )}

                {/* Shared fields: Perpetrator */}
                {killDetail.perpetratorKnown !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Alleged Perpetrators Known</h3>
                    <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                      killDetail.perpetratorKnown ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {killDetail.perpetratorKnown ? '✓ Yes' : '✗ No'}
                    </span>
                    {killDetail.perpetratorTypes && killDetail.perpetratorTypes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {killDetail.perpetratorTypes.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">{getViolationTypeLabel(t)}</span>
                        ))}
                        {aiMark(`violationDetails[${killDetail.id}].perpetratorTypes`)}
                      </div>
                    )}
                    {killDetail.perpetratorDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].perpetratorDetails`)} {killDetail.perpetratorDetails}</p>
                    )}
                  </div>
                )}

                {/* Shared fields: Legal Case */}
                {killDetail.legalCaseFiled !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Legal Case Filed: {killDetail.legalCaseFiled ? '✓ Yes' : '✗ No'}
                    </h3>
                    {killDetail.legalCaseFiled && killDetail.legalCaseDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].legalCaseDetails`)} {killDetail.legalCaseDetails}</p>
                    )}
                    {!killDetail.legalCaseFiled && killDetail.legalCaseWhyNot && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].legalCaseWhyNot`)} {killDetail.legalCaseWhyNot}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Shared fields: State Action */}
                {killDetail.stateActionUndertaken !== undefined && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      State Action Undertaken: {killDetail.stateActionUndertaken ? '✓ Yes' : '✗ No'}
                    </h3>
                    {killDetail.stateActionUndertaken && killDetail.stateActionDetails && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${killDetail.id}].stateActionDetails`)} {killDetail.stateActionDetails}</p>
                    )}
                    {!killDetail.stateActionUndertaken && killDetail.stateActionWhyNot && (
                      <div className="mt-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{killDetail.stateActionWhyNot}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Other HRV Details */}
          {(() => {
            const otherDetail = caseData.violationDetails?.find(vd => vd.violationType === 'OTHER');
            const hasOtherData = otherDetail ||
              (caseData.otherViolationTypes && caseData.otherViolationTypes.length > 0) ||
              caseData.otherViolationTypeDetails;
            return hasOtherData && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-purple-500">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Other Human Rights Violations<FieldSuggestion section="violations" />
              </h2>
              <div className="space-y-4">
                {caseData.otherViolationTypes && caseData.otherViolationTypes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Violation Types</h3>
                    <div className="flex flex-wrap gap-2">
                      {caseData.otherViolationTypes.map(v => (
                        <span key={v} className="px-3 py-1 rounded-lg text-sm bg-purple-100 text-purple-800">{getViolationTypeLabel(v)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {caseData.otherViolationTypeDetails && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Other (Specified)</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark('otherViolationTypeDetails')} {caseData.otherViolationTypeDetails}</p>
                  </div>
                )}

                {otherDetail && (
                  <>
                    {otherDetail.cause && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Cause</h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${otherDetail.id}].cause`)} {otherDetail.cause}</p>
                      </div>
                    )}

                    {/* Shared fields: Perpetrator */}
                    {otherDetail.perpetratorKnown !== undefined && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Alleged Perpetrators Known</h3>
                        <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          otherDetail.perpetratorKnown ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {otherDetail.perpetratorKnown ? '✓ Yes' : '✗ No'}
                        </span>
                        {otherDetail.perpetratorTypes && otherDetail.perpetratorTypes.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {otherDetail.perpetratorTypes.map(t => (
                              <span key={t} className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">{getViolationTypeLabel(t)}</span>
                            ))}
                            {aiMark(`violationDetails[${otherDetail.id}].perpetratorTypes`)}
                          </div>
                        )}
                        {otherDetail.perpetratorDetails && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{aiMark(`violationDetails[${otherDetail.id}].perpetratorDetails`)} {otherDetail.perpetratorDetails}</p>
                        )}
                      </div>
                    )}

                    {/* Shared fields: Legal Case */}
                    {otherDetail.legalCaseFiled !== undefined && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          Legal Case Filed: {otherDetail.legalCaseFiled ? '✓ Yes' : '✗ No'}
                        </h3>
                        {otherDetail.legalCaseFiled && otherDetail.legalCaseDetails && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${otherDetail.id}].legalCaseDetails`)} {otherDetail.legalCaseDetails}</p>
                        )}
                        {!otherDetail.legalCaseFiled && otherDetail.legalCaseWhyNot && (
                          <div className="mt-2">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${otherDetail.id}].legalCaseWhyNot`)} {otherDetail.legalCaseWhyNot}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Shared fields: State Action */}
                    {otherDetail.stateActionUndertaken !== undefined && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          State Action Undertaken: {otherDetail.stateActionUndertaken ? '✓ Yes' : '✗ No'}
                        </h3>
                        {otherDetail.stateActionUndertaken && otherDetail.stateActionDetails && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark(`violationDetails[${otherDetail.id}].stateActionDetails`)} {otherDetail.stateActionDetails}</p>
                        )}
                        {!otherDetail.stateActionUndertaken && otherDetail.stateActionWhyNot && (
                          <div className="mt-2">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark(`violationDetails[${otherDetail.id}].stateActionWhyNot`)}
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{otherDetail.stateActionWhyNot}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            );
          })()}

          {/* Perpetrators */}
          {caseData.perpetrators && caseData.perpetrators.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Perpetrators<FieldSuggestion section="perpetrators" /></h2>
              <div className="space-y-4">
                {caseData.perpetrators.map((perpetrator) => (
                  <div key={perpetrator.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{perpetrator.name}</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Type:</span> {perpetrator.type}
                      {perpetrator.isOrganization && (<span className="ml-4 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">Organization</span>)}
                    </div>
                    {perpetrator.affiliation && (<div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Affiliation:</span> {perpetrator.affiliation}</div>)}
                    {perpetrator.notes && (<div className="mt-2"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Notes:</span><p className="text-sm text-gray-700 dark:text-gray-300">{perpetrator.notes}</p></div>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Impact Assessment */}
          {(caseData.familyImpact || caseData.communityImpact) && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Impact Assessment</h2>
              <div className="space-y-4">
                {caseData.familyImpact && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Family Impact<FieldSuggestion field="familyImpact" />{aiMark('familyImpact')}</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{caseData.familyImpact}</p>
                  </div>
                )}
                {caseData.communityImpact && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Community Impact<FieldSuggestion field="communityImpact" />{aiMark('communityImpact')}</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{caseData.communityImpact}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Development Project Information */}
          {(caseData.isRelatedToDevelopmentProject || caseData.developmentProjectName || caseData.developmentProjectType || caseData.developmentProjectDescription || caseData.developmentProjectStartYear || caseData.developmentProjectSource) && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Development Project Information</h2>
              <div className="space-y-3">
                {caseData.isRelatedToDevelopmentProject && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800">This case is related to a development project</p>
                  </div>
                )}
                {caseData.developmentProjectType && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project Type:</span> {{ CONSERVATION: 'Conservation', ENERGY: 'Energy', TRANSITION_ENERGY: 'Transition Energy', MINING: 'Mining', TRANSITION_MINERAL_MINING: 'Transition Mineral Mining', AGRIBUSINESS: 'Agribusiness', TOURISM: 'Tourism', INFRASTRUCTURE: 'Infrastructure', DEFORESTATION: 'Miscellaneous Projects Causing Deforestation', NO_INFORMATION: 'No Information' }[caseData.developmentProjectType] || caseData.developmentProjectType}</div>)}
                {caseData.developmentProjectName && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project Name:</span> {caseData.developmentProjectName}</div>)}
                {caseData.developmentProjectDescription && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Description:</span><p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{caseData.developmentProjectDescription}</p></div>)}
                {caseData.developmentProjectStartYear && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Start Year:</span> {caseData.developmentProjectStartYear}</div>)}
                {caseData.developmentProjectSource && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Source:</span><p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{caseData.developmentProjectSource}</p></div>)}
                {caseData.developmentProjectWebsiteLink && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Website:</span><a href={caseData.developmentProjectWebsiteLink} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 underline break-all">{caseData.developmentProjectWebsiteLink}</a></div>)}
              </div>
            </div>
          )}

          {/* Investigations */}
          {caseData.investigationConducted !== undefined && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Investigations</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Investigation Conducted:</span>{aiMark('investigationConducted')}
                  <span className={`ml-2 px-3 py-1 rounded-lg text-xs font-medium ${caseData.investigationConducted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {caseData.investigationConducted ? 'Yes' : 'No'}
                  </span>
                </div>
                {caseData.investigationConducted && caseData.investigationDetails && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark('investigationDetails')} {caseData.investigationDetails}</p>
                )}
                {!caseData.investigationConducted && caseData.investigationWhyNot && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark('investigationWhyNot')}
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{caseData.investigationWhyNot}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Advocacy Actions */}
          {(caseData.advocacyDetails || caseData.advocacyUndertaken !== undefined) && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Advocacy & Response Actions</h2>
              {caseData.advocacyUndertaken !== undefined && (
                <div className="mb-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Advocacy Undertaken:</span>{aiMark('advocacyUndertaken')}
                  <span className={`ml-2 px-3 py-1 rounded-lg text-xs font-medium ${
                    caseData.advocacyUndertaken ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {caseData.advocacyUndertaken ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {caseData.advocacyUndertaken && caseData.advocacyDetails && (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{aiMark('advocacyDetails')} {caseData.advocacyDetails}</p>
              )}
              {!caseData.advocacyUndertaken && caseData.advocacyWhyNot && (
                <div className="mt-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark('advocacyWhyNot')}
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{caseData.advocacyWhyNot}</p>
                </div>
              )}
            </div>
          )}

          {/* Tags */}
          {caseData.tags && caseData.tags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {caseData.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Auto-filled fields with no inline anchor — catch-all at the bottom of the case details */}
          {aiLeftovers()}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Suggestions from related sources — clickable card → right slide-over */}
          <SuggestionsCard />

          {/* AI Assisted Verification — bot-produced (auto-sourced) secondary-data cases only */}
          {(user?.role === 'ADMIN' || user?.role === 'APPROVER') && caseData.isPrimaryData === false && caseData.isAutoSourced === true && (
            <AiValidationPanel caseId={id!} caseData={caseData as Record<string, any>} onChange={fetchCase} />
          )}

          {/* Case Information - Editable */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Case Information</h3>
              {canUpdateStatus() && !editingCaseInfo && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingCaseInfo(true);
                    setEditStatus(caseData.status);
                    setEditPriority(caseData.priority);
                    setEditReportedDate(caseData.reportedDate ? new Date(caseData.reportedDate).toISOString().split('T')[0] : '');
                    setEditAssignee(caseData.assignedTo?.id || '');
                    fetchAvailableUsers();
                  }}
                  className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            {editingCaseInfo ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="FOR_VALIDATION">For Validation</option>
                    <option value="IN_RESEARCH">In Research</option>
                    <option value="APPROVED">Approved</option>
                    <option value="FOR_APPROVAL">For Approval</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</label>
                  <select
                    value={editPriority}
                    onChange={(e) => setEditPriority(parseInt(e.target.value))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  >
                    <option value={1}>Low</option>
                    <option value={2}>Med-low</option>
                    <option value={3}>Medium</option>
                    <option value={4}>Med-high</option>
                    <option value={5}>High</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</label>
                  <select
                    value={editAssignee}
                    onChange={(e) => setEditAssignee(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {availableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Date of Submission</label>
                  <input
                    type="date"
                    value={editReportedDate}
                    onChange={(e) => setEditReportedDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (editStatus !== caseData.status) await updateCaseStatus(editStatus);
                      if (editPriority !== caseData.priority) await updateCasePriority(editPriority);

                      const currentAssigneeId = caseData.assignedTo?.id || '';
                      if (editAssignee !== currentAssigneeId) await handleAssignment(editAssignee || null);

                      const currentReportedDate = caseData.reportedDate ? new Date(caseData.reportedDate).toISOString().split('T')[0] : '';
                      if (editReportedDate !== currentReportedDate) {
                        setUpdating(true);
                        try {
                          const response = await API.put(`/api/cases/${id}`, { reportedDate: editReportedDate });
                          setCaseData(response.data.case);
                        } catch (err: any) {
                          showToast('error', err.response?.data?.message || 'Failed to update reported date');
                        } finally {
                          setUpdating(false);
                        }
                      }
                      setEditingCaseInfo(false);
                    }}
                    disabled={updating}
                    className="flex-1 px-3 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingCaseInfo(false)}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      { DRAFT: 'bg-gray-100 text-gray-800', FOR_VALIDATION: 'bg-blue-100 text-blue-800', IN_RESEARCH: 'bg-yellow-100 text-yellow-800', APPROVED: 'bg-green-100 text-green-800', FOR_APPROVAL: 'bg-purple-100 text-purple-800', REJECTED: 'bg-red-100 text-red-800' }[caseData.status] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {caseData.status === 'FOR_VALIDATION' ? 'For Validation' : caseData.status === 'IN_RESEARCH' ? 'In Research' : caseData.status === 'FOR_APPROVAL' ? 'For Approval' : caseData.status.charAt(0) + caseData.status.slice(1).toLowerCase()}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</dt>
                  <dd className="mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      { 1: 'bg-green-100 text-green-800', 2: 'bg-green-100 text-green-800', 3: 'bg-yellow-100 text-yellow-800', 4: 'bg-orange-100 text-orange-800', 5: 'bg-red-100 text-red-800' }[caseData.priority] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {getPriorityLabel(caseData.priority)}
                    </span>
                  </dd>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted By</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {caseData.submittedBy.firstName} {caseData.submittedBy.lastName}
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{caseData.submittedBy.role}</span>
                  </dd>
                </div>
                {caseData.reviewedBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Reviewed By</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{caseData.reviewedBy.firstName} {caseData.reviewedBy.lastName}</dd>
                  </div>
                )}
                {caseData.status === 'REJECTED' && caseData.rejectionReason && (
                  <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">Rejection reason</dt>
                    {caseData.rejectionCategory && (
                      <dd className="mt-1"><span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">{caseData.rejectionCategory}</span></dd>
                    )}
                    <dd className="text-sm text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-wrap">{caseData.rejectionReason}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</dt>
                  <dd className="mt-1">
                    {caseData.assignedTo ? (
                      <>
                        <span className="text-sm text-gray-900 dark:text-gray-100">{caseData.assignedTo.firstName} {caseData.assignedTo.lastName}</span>
                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{caseData.assignedTo.role}</span>
                        {caseData.assignedAt && (
                          <span className="block text-xs text-gray-500 mt-1">Assigned {new Date(caseData.assignedAt).toLocaleDateString()}</span>
                        )}
                      </>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500">Unassigned</span>
                    )}
                  </dd>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Date of Submission</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{new Date(caseData.reportedDate).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{new Date(caseData.createdAt).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{new Date(caseData.updatedAt).toLocaleDateString()}</dd>
                </div>
              </dl>
            )}

          </div>

          {/* Deduplication — AI operation, ADMIN/APPROVER only */}
          {(user?.role === 'ADMIN' || user?.role === 'APPROVER') && (
            <DeduplicationPanel caseId={id!} onSummary={handleDedupSummary} />
          )}

          {/* Internal Comments */}
          <CaseAssignment
            caseId={id!}
            currentAssignee={caseData.assignedTo}
            onAssignmentChange={fetchCase}
          />

          {/* Documents */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Documents ({documents.length})
            </h3>
            {documentsLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                Loading...
              </div>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-500">No documents uploaded</p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(doc.fileSize / 1024 / 1024).toFixed(2)} MB • {doc.fileType} •{' '}
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadDocument(doc.id)}
                        className="text-emerald-600 hover:text-emerald-800 text-sm ml-2"
                      >
                        Download
                      </button>
                    </div>
                    {(doc.category || doc.description || doc.documentDate) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                        {doc.category && <span>Category: <span className="text-gray-700 font-medium">{doc.category}</span></span>}
                        {doc.documentDate && <span>Date: <span className="text-gray-700 font-medium">{new Date(doc.documentDate).toLocaleDateString()}</span></span>}
                        {doc.authorSource && <span>Source: <span className="text-gray-700 font-medium">{doc.authorSource}</span></span>}
                        {doc.language && <span>Language: <span className="text-gray-700 font-medium">{doc.language}</span></span>}
                        {doc.confidentiality && doc.confidentiality !== 'INTERNAL' && (
                          <span className={`px-1.5 py-0.5 rounded font-medium ${doc.confidentiality === 'RESTRICTED' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                            {doc.confidentiality}
                          </span>
                        )}
                      </div>
                    )}
                    {doc.description && (
                      <p className="text-xs text-gray-600">{doc.description}</p>
                    )}
                    {doc.sensitivityWarnings && doc.sensitivityWarnings.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {doc.sensitivityWarnings.map((w: string) => (
                          <span key={w} className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">{w}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          {caseData.tags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {caseData.tags.map((tag, index) => (
                  <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sources — original + accumulated (follow-up / corroboration / archive) */}
          {(caseData.sourceInformation || (caseData.metadata?.additionalSources?.length ?? 0) > 0) && (() => {
            const kindMeta: Record<string, { label: string; color: string }> = {
              ORIGIN: { label: 'Original', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
              MERGE: { label: 'Follow-up', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
              CORROBORATION: { label: 'Corroboration', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
              ARCHIVE: { label: 'Archive', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
              MANUAL: { label: 'Manual', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
            };
            const extra = (caseData.metadata?.additionalSources ?? []).filter(
              (s) => s && s.url && s.url !== caseData.sourceInformation
            );
            const rows: Array<{ url: string; kind?: string; addedAt?: string }> = [
              ...(caseData.sourceInformation ? [{ url: caseData.sourceInformation, kind: 'ORIGIN' }] : []),
              ...extra,
            ];
            return (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Sources ({rows.length})
                </h3>
                <ul className="space-y-2">
                  {(showAllSources ? rows : rows.slice(0, 5)).map((s, i) => {
                    const m = kindMeta[s.kind ?? 'MANUAL'] ?? kindMeta.MANUAL;
                    const host = (s.url || '').replace(/^https?:\/\//, '').replace(/^www\./, '');
                    return (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full font-medium shrink-0 ${m.color}`}>
                          {m.label}
                        </span>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 break-all"
                          title={s.url}
                        >
                          {host.length > 60 ? host.slice(0, 60) + '…' : host}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                {rows.length > 5 && (
                  <button
                    onClick={() => setShowAllSources((v) => !v)}
                    className="mt-3 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"
                  >
                    {showAllSources ? 'See less' : `See more (${rows.length - 5})`}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>

    {/* Reject modal — a required ground + free-text note, recorded on the case */}
    {rejectOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject case</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Record why this case is being rejected. This is kept on the case.</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Ground</label>
              <select
                value={rejectCategory}
                onChange={(e) => setRejectCategory(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              >
                <option value="">Select a ground…</option>
                {['Out of scope', 'Not a documentable incident', 'Unverifiable / insufficient evidence', 'Inaccurate or fabricated', 'Incomplete data', 'Duplicate', 'Consent or safety concern', 'Other'].map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain why this case cannot be published…"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={() => setRejectOpen(false)}
              disabled={updating}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={rejectCase}
              disabled={updating || !rejectNote.trim()}
              className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {updating ? 'Rejecting…' : 'Reject case'}
            </button>
          </div>
        </div>
      </div>
    )}
    </SuggestionsProvider>
  );
};

export default CaseDetail;